import React, { useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "@/hooks/redux";
import {
  fetchDashboardStats,
  fetchUsageMetrics,
  fetchUsers,
  fetchPlans,
  fetchAdmins,
} from "@/store/slices/adminSlice";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Formik, Form, Field, ErrorMessage } from "formik";
import { adminPasswordResetSchema } from "@/schemas/adminSchemas";
import type { AdminPasswordResetFormData } from "@/schemas/adminSchemas";
import {
  Users,
  BarChart3,
  Activity,
  CreditCard,
  Shield,
  FileText,
  Code2,
  Bot,
  Coins,
  AlertTriangle,
  Brain,
  MessagesSquare,
  Key,
  User,
  LogOut,
  MoreVertical,
} from "lucide-react";
import { toast } from "sonner";
import { useAdminAuth } from "../../hooks/useAdminAuth";

import { resetAdminSessionHandler } from "@/services/adminService";
import adminService from "@/services/adminService";
import { ThemeToggle } from "../../components/theme-toggle";

import {
  CreatePlanDialog,
  EditPlanDialog,
  ViewPlanDialog,
  DeletePlanDialog,
} from "../../components/admin/PlanDialogs";
import { UserInfoDialog } from "../../components/admin/UserInfoDialog";
import type { AdminUser } from "../../components/admin/UsersDataTable";
import type { AdminPlan, PlatformUser } from "@/services/adminService";
import { useTheme } from "@/hooks/useTheme";

// Import new tab components
import { OverviewTab } from "../../components/admin/tabs/OverviewTab";

import { UsersTab } from "../../components/admin/tabs/UsersTab";
import { AnalyticsTab } from "../../components/admin/tabs/AnalyticsTab";
import { PlansTab } from "../../components/admin/tabs/PlansTab";
import { BillingTab } from "../../components/admin/tabs/BillingTab";
import { AdminsTab } from "../../components/admin/tabs/AdminsTab";
import { LegalContentTab } from "../../components/admin/tabs/LegalContentTab";
import { SilaeVariablesTab } from "../../components/admin/tabs/SilaeVariablesTab";
import { AiAgentConfigTab } from "../../components/admin/tabs/AiAgentConfigTab";
import { AiCreditPacksTab } from "../../components/admin/tabs/AiCreditPacksTab";
import { AiFailuresTab } from "../../components/admin/tabs/AiFailuresTab";
import { AiFeedbacksTab } from "../../components/admin/tabs/AiFeedbacksTab";
import { AiSkillTab } from "../../components/admin/tabs/AiSkillTab";

interface DashboardStats {
  totalConversations: number;
  totalUsers: number;
  monthlyUsage: number;
  growthRate: number;
}

export const AdminDashboard: React.FC = () => {
  const dispatch = useAppDispatch();
  const { logoutAdmin } = useAdminAuth();
  const { isDark } = useTheme();
  // Tab state management
  const [activeTab, setActiveTab] = useState("overview");

  // Password reset dialog state
  const [resetPasswordDialogOpen, setResetPasswordDialogOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isVerifyingPassword, setIsVerifyingPassword] = useState(false);

  // Plan dialog state
  const [createPlanDialogOpen, setCreatePlanDialogOpen] = useState(false);
  const [editPlanDialogOpen, setEditPlanDialogOpen] = useState(false);
  const [viewPlanDialogOpen, setViewPlanDialogOpen] = useState(false);
  const [deletePlanDialogOpen, setDeletePlanDialogOpen] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<AdminPlan | null>(null);

  // User dialog state
  const [userInfoDialogOpen, setUserInfoDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<AdminUser | null>(null);

  const {
    dashboardStats,
    usageMetrics,
    users,
    plans,
    admins,
    dashboardLoading,
    usersLoading,
    plansLoading,
    adminsLoading,
    dashboardError,
    usersError,
    plansError,
    adminsError,
    adminUser,
    isAuthenticated,
  } = useAppSelector((state) => state.admin);

  // Load dashboard data on component mount
  useEffect(() => {
    const loadDashboardData = async () => {
      try {
        // Reset session handler to allow proper error handling
        resetAdminSessionHandler();

        // Load all dashboard data in parallel
        await Promise.all([
          dispatch(fetchDashboardStats()),
          dispatch(fetchUsageMetrics("30d")),

          dispatch(fetchUsers()),
          dispatch(fetchPlans()),
          dispatch(fetchAdmins()), // Add admin fetching
        ]);
      } catch (error) {
        console.error("[AdminDashboard] Error loading dashboard data:", error);
        toast.error("Erreur lors du chargement des données du tableau de bord");
      }
    };

    if (isAuthenticated) {
      loadDashboardData();
    }
  }, [dispatch, isAuthenticated]);

  const handleLogout = async () => {
    try {
      await logoutAdmin();
      toast.success("Déconnexion réussie");
    } catch (error) {
      console.error("Erreur lors de la déconnexion:", error);
      toast.error("Erreur lors de la déconnexion");
    }
  };

  const handleTabChange = (tabValue: string) => {
    setActiveTab(tabValue);
  };

  const handleViewPlan = (plan: AdminPlan) => {
    setSelectedPlan(plan);
    setViewPlanDialogOpen(true);
  };

  const handleEditPlan = (plan: AdminPlan) => {
    setSelectedPlan(plan);
    setEditPlanDialogOpen(true);
  };

  const handleDeletePlan = (plan: AdminPlan) => {
    setSelectedPlan(plan);
    setDeletePlanDialogOpen(true);
  };

  const handleCreatePlan = () => {
    setCreatePlanDialogOpen(true);
  };

  const refetchPlans = () => {
    dispatch(fetchPlans());
  };

  const refetchUsers = () => {
    dispatch(fetchUsers());
  };

  const handleViewUser = (user: AdminUser) => {
    setSelectedUser(user);
    setUserInfoDialogOpen(true);
  };

  // Password reset functions
  const handleResetPassword = () => {
    setResetPasswordDialogOpen(true);
  };

  const handlePasswordReset = async (values: AdminPasswordResetFormData) => {
    if (!adminUser?.id) {
      toast.error("Impossible d'identifier l'administrateur connecté");
      return;
    }

    try {
      setIsSubmitting(true);

      // Use the imported adminService

      // First, verify current password
      setIsVerifyingPassword(true);
      const verifyResult = await adminService.verifyCurrentPassword(
        values.currentPassword,
      );

      if (!verifyResult.success || !verifyResult.valid) {
        throw new Error("Mot de passe actuel incorrect");
      }

      setIsVerifyingPassword(false);

      // Then reset password
      const result = await adminService.resetAdminPassword(
        adminUser.id,
        values.newPassword,
      );

      if (result.success) {
        toast.success("Votre mot de passe a été réinitialisé avec succès");
        setResetPasswordDialogOpen(false);
      } else {
        throw new Error(result.message || "Erreur lors de la réinitialisation");
      }
    } catch (error: unknown) {
      console.error(
        "Erreur lors de la réinitialisation du mot de passe:",
        error,
      );
      toast.error(
        error instanceof Error
          ? error.message
          : "Erreur lors de la réinitialisation du mot de passe",
      );
    } finally {
      setIsSubmitting(false);
      setIsVerifyingPassword(false);
    }
  };

  return (
    <div className="min-h-screen font-sans  ">
      <header className="bg-white dark:bg-background shadow-sm border-b border-gray-200 dark:border-gray-700">
        <div className="px-6 py-4 flex justify-between items-center">
          <div>
            <div className="flex items-center">
              <img
                src={
                  isDark
                    ? "/assets/logo_full_dark.webp"
                    : "/assets/logo_full_light.webp"
                }
                alt="logo"
                className=" h-10 mr-2"
              />
            </div>
          </div>
          <div className="flex flex-col items-center">
            <p className="text-sm text-gray-600 dark:text-gray-400 font-mono">
              Bienvenue
            </p>
            <p className="text-md text-gray-600 dark:text-gray-400 font-medium font-mono">
              {adminUser?.firstName} {adminUser?.lastName}
            </p>
          </div>
          <div className="flex items-center space-x-4">
            {/* Session status indicator */}
            <div className="flex items-center space-x-2">
              <div
                className={`w-2 h-2 rounded-full ${
                  isAuthenticated ? "bg-green-500" : "bg-red-500"
                }`}
                title={isAuthenticated ? "Session active" : "Session inactive"}
              />
              <span className="text-xs text-gray-500 dark:text-gray-400">
                {isAuthenticated ? "Connecté" : "Déconnecté"}
              </span>
            </div>
            <ThemeToggle />

            {/* Admin Menu Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  className="flex items-center gap-2"
                >
                  <User className="h-4 w-4" />
                  <span className="hidden sm:inline">
                    {adminUser?.firstName}
                  </span>
                  <MoreVertical className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>Mon Compte</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={handleResetPassword}
                  className="flex items-center gap-2"
                >
                  <Key className="h-4 w-4" />
                  Réinitialiser le mot de passe
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={handleLogout}
                  className="flex items-center gap-2 text-red-600 focus:text-red-600 dark:text-red-400"
                >
                  <LogOut className="h-4 w-4 text-red-600 dark:text-red-400" />
                  Déconnexion
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      <div className="p-6">
        <Tabs
          value={activeTab}
          onValueChange={handleTabChange}
          className="w-full"
        >
          <TabsList className="grid w-full grid-cols-4 lg:grid-cols-13 mb-6">
            <TabsTrigger
              value="overview"
              className="flex items-center space-x-2"
            >
              <Activity className="h-4 w-4" />
              <span className="hidden sm:inline">Vue d'ensemble</span>
            </TabsTrigger>

            <TabsTrigger value="users" className="flex items-center space-x-2">
              <Users className="h-4 w-4" />
              <span className="hidden sm:inline">Utilisateurs</span>
            </TabsTrigger>
            <TabsTrigger
              value="analytics"
              className="flex items-center space-x-2"
            >
              <BarChart3 className="h-4 w-4" />
              <span className="hidden sm:inline">Analyses</span>
            </TabsTrigger>
            <TabsTrigger value="plans" className="flex items-center space-x-2">
              <CreditCard className="h-4 w-4" />
              <span className="hidden sm:inline">Plans</span>
            </TabsTrigger>
            <TabsTrigger
              value="billing"
              className="flex items-center space-x-2"
            >
              <CreditCard className="h-4 w-4" />
              <span className="hidden sm:inline">Produits Stripe</span>
            </TabsTrigger>
            <TabsTrigger value="admins" className="flex items-center space-x-2">
              <Shield className="h-4 w-4" />
              <span className="hidden sm:inline">Administrateurs</span>
            </TabsTrigger>
            <TabsTrigger value="legal" className="flex items-center space-x-2">
              <FileText className="h-4 w-4" />
              <span className="hidden sm:inline">Contenu Légal</span>
            </TabsTrigger>
            <TabsTrigger value="silae" className="flex items-center space-x-2">
              <Code2 className="h-4 w-4" />
              <span className="hidden sm:inline">Variables SILAE</span>
            </TabsTrigger>
            <TabsTrigger value="ai-agent" className="flex items-center space-x-2">
              <Bot className="h-4 w-4" />
              <span className="hidden sm:inline">Agent IA</span>
            </TabsTrigger>
            <TabsTrigger
              value="ai-credits"
              className="flex items-center space-x-2"
            >
              <Coins className="h-4 w-4" />
              <span className="hidden sm:inline">Crédits IA</span>
            </TabsTrigger>
            <TabsTrigger
              value="ai-failures"
              className="flex items-center space-x-2"
            >
              <AlertTriangle className="h-4 w-4" />
              <span className="hidden sm:inline">Échecs IA</span>
            </TabsTrigger>
            <TabsTrigger
              value="ai-feedbacks"
              className="flex items-center space-x-2"
            >
              <MessagesSquare className="h-4 w-4" />
              <span className="hidden sm:inline">Feedbacks IA</span>
            </TabsTrigger>
            <TabsTrigger
              value="ai-skill"
              className="flex items-center space-x-2"
            >
              <Brain className="h-4 w-4" />
              <span className="hidden sm:inline">Skill SILAE</span>
            </TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="mt-6">
            <OverviewTab
              dashboardStats={dashboardStats as unknown as DashboardStats}
              dashboardLoading={dashboardLoading}
              dashboardError={dashboardError}
              onNavigateToTab={handleTabChange}
            />
          </TabsContent>

          <TabsContent value="users" className="mt-6">
            <UsersTab
              users={users as unknown as PlatformUser[]}
              usersLoading={usersLoading}
              usersError={usersError}
              onViewUser={handleViewUser}
              onRefreshUsers={refetchUsers}
            />
          </TabsContent>

          <TabsContent value="analytics" className="mt-6">
            <AnalyticsTab
              usageMetrics={usageMetrics}
              dashboardStats={dashboardStats}
              users={users}
            />
          </TabsContent>

          <TabsContent value="plans" className="mt-6">
            <PlansTab
              plans={plans}
              plansLoading={plansLoading}
              plansError={plansError}
              onCreatePlan={handleCreatePlan}
              onViewPlan={handleViewPlan}
              onEditPlan={handleEditPlan}
              onDeletePlan={handleDeletePlan}
            />
          </TabsContent>

          <TabsContent value="billing" className="mt-6">
            <BillingTab />
          </TabsContent>

          <TabsContent value="admins" className="mt-6">
            <AdminsTab
              admins={admins}
              adminsLoading={adminsLoading}
              adminsError={adminsError}
              currentAdminId={adminUser?.id}
            />
          </TabsContent>

          <TabsContent value="legal" className="mt-6">
            <LegalContentTab />
          </TabsContent>

          <TabsContent value="silae" className="mt-6">
            <SilaeVariablesTab />
          </TabsContent>

          <TabsContent value="ai-agent" className="mt-6">
            <AiAgentConfigTab />
          </TabsContent>

          <TabsContent value="ai-credits" className="mt-6">
            <AiCreditPacksTab />
          </TabsContent>

          <TabsContent value="ai-failures" className="mt-6">
            <AiFailuresTab />
          </TabsContent>

          <TabsContent value="ai-feedbacks" className="mt-6">
            <AiFeedbacksTab />
          </TabsContent>

          <TabsContent value="ai-skill" className="mt-6">
            <AiSkillTab />
          </TabsContent>
        </Tabs>
      </div>

      {/* Plan CRUD Dialogs */}
      <CreatePlanDialog
        open={createPlanDialogOpen}
        onOpenChange={setCreatePlanDialogOpen}
        onSuccess={refetchPlans}
      />

      <EditPlanDialog
        open={editPlanDialogOpen}
        onOpenChange={setEditPlanDialogOpen}
        plan={selectedPlan}
        onSuccess={refetchPlans}
      />

      <ViewPlanDialog
        open={viewPlanDialogOpen}
        onOpenChange={setViewPlanDialogOpen}
        plan={selectedPlan}
      />

      <DeletePlanDialog
        open={deletePlanDialogOpen}
        onOpenChange={setDeletePlanDialogOpen}
        plan={selectedPlan}
        onSuccess={refetchPlans}
      />

      {/* User Info Dialog */}
      <UserInfoDialog
        open={userInfoDialogOpen}
        onOpenChange={setUserInfoDialogOpen}
        user={selectedUser}
      />

      {/* Password Reset Dialog */}
      <Dialog
        open={resetPasswordDialogOpen}
        onOpenChange={setResetPasswordDialogOpen}
      >
        <DialogContent
          className="max-w-md"
          onOpenAutoFocus={(event) => event.preventDefault()}
        >
          <DialogHeader>
            <DialogTitle>Réinitialiser mon mot de passe</DialogTitle>
            <DialogDescription>
              Définissez un nouveau mot de passe pour votre compte
              administrateur
            </DialogDescription>
          </DialogHeader>
          <Formik
            initialValues={{
              currentPassword: "",
              newPassword: "",
              confirmPassword: "",
            }}
            validationSchema={adminPasswordResetSchema}
            onSubmit={handlePasswordReset}
          >
            {({ errors, touched, isValid, dirty }) => {
              return (
                <Form className="space-y-4">
                  <div>
                    <Label htmlFor="currentPassword">Mot de passe actuel</Label>
                    <Field
                      as={Input}
                      id="currentPassword"
                      name="currentPassword"
                      type="password"
                      placeholder="••••••••"
                      className={
                        errors.currentPassword && touched.currentPassword
                          ? "border-red-500"
                          : ""
                      }
                      autoFocus={false}
                    />
                    <ErrorMessage
                      name="currentPassword"
                      component="div"
                      className="text-red-500 text-xs mt-1"
                    />
                  </div>

                  <div>
                    <Label htmlFor="newPassword">Nouveau mot de passe</Label>
                    <Field
                      as={Input}
                      id="newPassword"
                      name="newPassword"
                      type="password"
                      placeholder="••••••••"
                      className={
                        errors.newPassword && touched.newPassword
                          ? "border-red-500"
                          : ""
                      }
                    />
                    <ErrorMessage
                      name="newPassword"
                      component="div"
                      className="text-red-500 text-xs mt-1"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Minimum 8 caractères avec majuscule, minuscule et chiffre
                    </p>
                  </div>

                  <div>
                    <Label htmlFor="confirmPassword">
                      Confirmer le nouveau mot de passe
                    </Label>
                    <Field
                      as={Input}
                      id="confirmPassword"
                      name="confirmPassword"
                      type="password"
                      placeholder="••••••••"
                      className={
                        errors.confirmPassword && touched.confirmPassword
                          ? "border-red-500"
                          : ""
                      }
                    />
                    <ErrorMessage
                      name="confirmPassword"
                      component="div"
                      className="text-red-500 text-xs mt-1"
                    />
                  </div>

                  <DialogFooter>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setResetPasswordDialogOpen(false)}
                      disabled={isSubmitting}
                    >
                      Annuler
                    </Button>
                    <Button
                      type="submit"
                      disabled={
                        isSubmitting ||
                        isVerifyingPassword ||
                        !isValid ||
                        !dirty
                      }
                    >
                      {isVerifyingPassword
                        ? "Vérification..."
                        : isSubmitting
                          ? "Réinitialisation..."
                          : "Réinitialiser"}
                    </Button>
                  </DialogFooter>
                </Form>
              );
            }}
          </Formik>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminDashboard;
